import React from 'react';
import { MCPServer } from '@/shared/types';

interface ServerListProps {
  servers: MCPServer[];
  selectedServer: MCPServer | null;
  loading: boolean;
  onSelectServer: (server: MCPServer) => void;
  onToggleServer: (serverId: string) => void;
  onTestConnection: (server: MCPServer) => void;
  onEditServer: (server: MCPServer) => void;
  onDeleteServer: (serverId: string) => void;
}

export const ServerList: React.FC<ServerListProps> = ({
  servers,
  selectedServer,
  loading,
  onSelectServer,
  onToggleServer,
  onTestConnection,
  onEditServer,
  onDeleteServer,
}) => {
  return (
    <div className="w-1/4 border-r border-gray-700 flex flex-col h-full">
      <div className="p-6 border-b border-gray-700 flex-shrink-0">
        <h3 className="text-lg font-semibold text-white mb-2">MCP Servers</h3>
        <p className="text-sm text-gray-400">Manage your MCP server connections</p>
      </div>
      <div className="flex-1 overflow-y-auto p-6 min-h-0">
        <div className="space-y-3">
          {servers.map((server) => (
            <div
              key={server.id}
              className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border ${
                selectedServer?.id === server.id
                  ? 'bg-blue-600/20 border-blue-500/50 shadow-lg shadow-blue-500/10'
                  : 'bg-gray-800/50 border-gray-700 hover:bg-gray-700/50 hover:border-gray-600'
              }`}
              onClick={() => onSelectServer(server)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-semibold text-white truncate">{server.name}</h4>
                  <p className="text-sm text-gray-400 mt-1 line-clamp-2">{server.description}</p>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      server.status === 'connected'
                        ? 'bg-green-500'
                        : server.status === 'error'
                        ? 'bg-red-500'
                        : 'bg-gray-500'
                    }`}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleServer(server.id);
                    }}
                    className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                      server.enabled
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-gray-600 hover:bg-gray-700 text-gray-300'
                    }`}
                  >
                    {server.enabled ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTestConnection(server);
                  }}
                  className="flex-1 text-xs px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
                >
                  Test Connection
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditServer(server);
                  }}
                  className="px-3 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteServer(server.id);
                  }}
                  className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
